import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import DigitalAssetInvitationContentDesktop from '@cohort/wallet/components/DigitalAssetInvitationContentDesktop';
import {DigitalAssetInvitationContentMobile} from '@cohort/wallet/components/DigitalAssetInvitationContentMobile';
import CenteredContent from '@cohort/wallet/components/layout/CenteredContent';
import AboutNftTransferPanel from '@cohort/wallet/components/space/AboutNftTransferPanel';
import {NftTransferAcceptButton} from '@cohort/wallet/components/space/nftTransfer/NftTransferAcceptButton';
import {Spinner} from '@cohort/wallet/components/Spinner';
import TitleWithLeadingIcon from '@cohort/wallet/components/TitleWithLeadingIcon';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useShopify from '@cohort/wallet/hooks/useShopify';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {useTransferInvitationContext} from '@cohort/wallet/hooks/useTransferInvitationContext';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import {NftTransferInvitationApi, OwnershipsApi} from '@cohort/wallet/lib/Endpoints';
import {
  getHomepageRoute,
  getMerchantSpaceRoute,
  getNftTransferInvitationLoginRoute,
} from '@cohort/wallet/lib/Pages';
import {poll} from '@cohort/wallet/lib/Utils';
import type {DigitalAssetTransferJobWDto} from '@cohort/wallet-schemas/ownership';
import {Check, X} from '@phosphor-icons/react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {match} from 'ts-pattern';

const DigitalAssetTransferInvitationPage: React.FC = () => {
  const navigate = useNavigate();
  const {isMobile} = useScreenSizeContext();
  const user = useUserStore(store => store.user);
  const {accentColor} = useThemeContext();
  const {triggerRefresh} = useSpaceContext();
  const shopify = useShopify();
  const invitation = useTransferInvitationContext();
  const {t} = useTranslation('pages', {keyPrefix: 'space.nftTransferInvitationPage'});
  const location = useLocation();
  const hasInitiatedTransfer = useRef(false);

  const acceptDigitalAssetTransferInvitation = useApi(
    NftTransferInvitationApi.acceptDigitalAssetTransferInvitation
  );
  const getNftTransferJob = useApi(OwnershipsApi.getNftTransferJob);

  const [isTransferringNft, setIsTransferringNft] = useState(false);
  const [nftTransferError, setNftTransferError] = useState(false);
  const [successfulNftTransfer, setSuccessfulNftTransfer] = useState(false);

  const handleAcceptNftTransferInvitation = useCallback(async () => {
    setIsTransferringNft(true);

    try {
      const digitalAssetTransferJob: DigitalAssetTransferJobWDto =
        await acceptDigitalAssetTransferInvitation(invitation.id);
      const successfulJob = async (): Promise<true | undefined> => {
        const job = await getNftTransferJob(digitalAssetTransferJob.id);
        if (job.status === 'success') {
          return true;
        }
        throw new Error('unavailable');
      };
      await poll(successfulJob, 1000, 180); // 1 second * 180 retries = 3min.
      await triggerRefresh();
      setIsTransferringNft(false);
      setSuccessfulNftTransfer(true);
    } catch (err: unknown) {
      void err;
      setIsTransferringNft(false);
      setNftTransferError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitation.id]);

  useEffect(() => {
    if (user && location.state?.fromLogin && !isTransferringNft && !hasInitiatedTransfer.current) {
      hasInitiatedTransfer.current = true;
      handleAcceptNftTransferInvitation();
    }
  }, [user, handleAcceptNftTransferInvitation, location.state, isTransferringNft]);

  const handleAcceptNftButtonClicked = async (): Promise<void> => {
    if (user === null) {
      if (shopify.active) {
        shopify.signInRedirect();
      } else {
        navigate(getNftTransferInvitationLoginRoute(invitation.id));
      }
    } else {
      await handleAcceptNftTransferInvitation();
    }
  };

  const orderAccepted = invitation.status === 'accepted' || successfulNftTransfer;
  const orderImpossible = invitation.status === 'cancelled' || invitation.status === 'expired';

  const DigitalAssetContent = isMobile
    ? DigitalAssetInvitationContentMobile
    : DigitalAssetInvitationContentDesktop;

  const content = match({
    orderAccepted,
    orderImpossible,
    nftTransferError,
    isTransferringNft,
  })
    .with({isTransferringNft: true}, () => (
      <CenteredContent>
        <TitleWithLeadingIcon
          testId="transfer-in-progress"
          title={t('nftTransfertprocessingTitle')}
          subtitle={t('nftTransfertprocessingSubtitle')}
          icon={<Spinner color={accentColor} />}
        />
      </CenteredContent>
    ))
    .with({nftTransferError: true}, () => (
      <CenteredContent>
        <TitleWithLeadingIcon
          title={t('nftTransferErrorTitle')}
          subtitle={t('nftTransferErrorSubtitle')}
          icon={<X className="h-11 w-11" style={{color: accentColor}} />}
        >
          <PrimaryButton
            onClick={() => {
              navigate(getHomepageRoute(), {replace: true});
            }}
            className="w-full md:w-1/3"
            text={t('nftTransferErrorCtaText')}
            type="submit"
            tracking={{namespace: 'digitalAssets.transfer.error'}}
          />
        </TitleWithLeadingIcon>
      </CenteredContent>
    ))
    .with({orderAccepted: true}, () => (
      <CenteredContent>
        <TitleWithLeadingIcon
          testId="transfer-success"
          title={t('orderAcceptedTitle')}
          subtitle={t('orderAcceptedSubtitle')}
          icon={<Check className="h-11 w-11" style={{color: accentColor}} />}
        >
          <PrimaryButton
            data-testid="transfer-success-cta"
            onClick={async () => {
              if (shopify.active) {
                shopify.spaceRedirect();
              } else {
                await triggerRefresh();
                navigate(getMerchantSpaceRoute(), {replace: true});
              }
            }}
            className="w-full md:w-1/3"
            text={t('orderAcceptedCtaText')}
            type="submit"
            tracking={{namespace: 'digitalAssets.transfer.success'}}
          />
        </TitleWithLeadingIcon>
      </CenteredContent>
    ))
    .with({orderImpossible: true}, () => (
      <CenteredContent>
        <TitleWithLeadingIcon
          title={t('impossibleOrderTitle')}
          subtitle={t('impossibleOrderSubtitle')}
          icon={<X className="h-11 w-11" style={{color: accentColor}} />}
        >
          <PrimaryButton
            onClick={() => {
              navigate(getHomepageRoute(), {replace: true});
            }}
            className="w-full md:w-1/3"
            text={t('impossibleOrderCtaText')}
            type="submit"
            tracking={{namespace: 'digitalAssets.transfer.error'}}
          />
        </TitleWithLeadingIcon>
      </CenteredContent>
    ))
    .otherwise(() => (
      <DigitalAssetContent
        nftTransferInvitation={invitation}
        button={
          <NftTransferAcceptButton
            onAcceptNftButtonClicked={async () => handleAcceptNftButtonClicked()}
          />
        }
      />
    ));

  return (
    <SpacePage
      title={t('pageTitle')}
      tracking={{
        namespace: 'digitalAssets.transfer',
        metadata: {
          invitationId: invitation.id,
        },
      }}
      leftPanel={
        <AboutNftTransferPanel
          transferCtaEnabled={
            !isTransferringNft &&
            !orderImpossible &&
            !nftTransferError &&
            !successfulNftTransfer &&
            !orderAccepted
          }
          nftTransferInvitation={invitation}
          handleAcceptNftButtonClicked={async () => handleAcceptNftButtonClicked()}
        />
      }
    >
      <div className="h-full">{content}</div>
    </SpacePage>
  );
};

export default DigitalAssetTransferInvitationPage;
